import { useEffect, useState } from "react";
import API from "../lib/api";
import { apiRelativePath } from "../lib/fileOpen";

/**
 * Authenticated <img> component.
 *
 * Plain <img src="/api/..."> tags do NOT carry Authorization headers and, when
 * the backend is on a different origin from the SPA, browsers strip cookies
 * from img fetches by default. We fetch the bytes with our axios instance
 * (which already handles bearer token + credentials + refresh) and hand the
 * browser a local object URL it can render without any further auth.
 *
 * Uses a module-level LRU cache so the same image isn't refetched when it
 * appears multiple times on the page (common in gallery + row layouts).
 */

const CACHE = new Map(); // key = path, value = { objectUrl, bytes, refCount }
const MAX_CACHE = 80;

function evictOldest() {
  while (CACHE.size > MAX_CACHE) {
    const firstKey = CACHE.keys().next().value;
    const entry = CACHE.get(firstKey);
    if (entry) {
      URL.revokeObjectURL(entry.objectUrl);
      CACHE.delete(firstKey);
    }
  }
}

export default function AuthImage({ path, alt, className, onError, onLoad, ...rest }) {
  // Absolute / blob / data URLs need no auth fetch — render directly.
  const passthrough = path && /^(https?:|blob:|data:)/i.test(path);
  const [src, setSrc] = useState(() => (path && CACHE.get(path)?.objectUrl) || null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!path || passthrough) { setSrc(null); setFailed(false); return; }

    // Cache hit — use immediately
    const cached = CACHE.get(path);
    if (cached) { setSrc(cached.objectUrl); setFailed(false); return; }

    let cancelled = false;
    setFailed(false);
    setSrc(null);

    (async () => {
      try {
        // Stored URLs may already carry the "/api" prefix; baseURL adds it too.
        const res = await API.get(apiRelativePath(path), { responseType: "blob" });
        if (cancelled) return;
        const objectUrl = URL.createObjectURL(res.data);
        CACHE.set(path, { objectUrl });
        evictOldest();
        setSrc(objectUrl);
      } catch (e) {
        if (cancelled) return;
        setFailed(true);
        onError?.(e);
      }
    })();

    return () => { cancelled = true; };
  }, [path, passthrough, onError]);

  if (passthrough) {
    return <img src={path} alt={alt} className={className} onLoad={onLoad} onError={onError} {...rest} />;
  }
  if (failed || !path) {
    return (
      <div className={`flex items-center justify-center bg-slate-100 dark:bg-slate-800 text-slate-400 ${className || ""}`} title="Preview unavailable">
        <span className="text-[10px]">no preview</span>
      </div>
    );
  }
  if (!src) {
    return <div className={`animate-pulse bg-slate-200 dark:bg-slate-700 ${className || ""}`} />;
  }
  return <img src={src} alt={alt} className={className} onLoad={onLoad} {...rest} />;
}
